<script lang="ts" setup>
import { computed } from 'vue';

import type { Json } from '@nzyme/utils/toJson.js';
import { useService } from '@nzyme/vue-ioc/useService.js';
import type { ChatAgentQuestion } from '@sigma/chat-common/ChatEvent.js';
import { defineChatEventProps } from '@sigma/client/chat/defineChatEventProps.js';
import { useAnswerChatQuestion } from '@sigma/client/chat/useAnswerChatQuestion.js';
import type { FormInputValue } from '@sigma/common/forms/FormInput.js';
import ChatEventWrapper from '@sigma/ui/chat/ChatEventWrapper.vue';
import ChatQuestion from '@sigma/ui/chat/forms/ChatQuestion.vue';

import chatCss from './chat.module.scss';
import { ChatListStore } from './ChatListStore.js';

const props = defineProps(defineChatEventProps<ChatAgentQuestion>());

const chatListStore = useService(ChatListStore);

/** Whether this question is the last event in the chat (show input only then). */
const isLastEvent = computed(() => {
  const events = props.chat.events;
  return events[events.length - 1]?.seq === props.event.seq;
});

const { submit } = useAnswerChatQuestion({
  chat: props.chat,
  chatId: props.chat.chatId,
  author: { type: 'PATIENT' },
  markRead: (id, seq) => chatListStore.markRead(id, seq),
});

async function handleSubmit(answer: Json<FormInputValue>) {
  if (!isLastEvent.value) {
    return;
  }

  await submit(answer, props.event.seq);
}
</script>

<template>
  <ChatEventWrapper
    tid="agent-question"
    :event="event"
    :class="chatCss.container"
  >
    <ChatQuestion
      :config="event.input"
      :timestamp="event.timestamp"
      :show-input="isLastEvent"
      @submit="handleSubmit"
    />
  </ChatEventWrapper>
</template>
